import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './Tooltip.module.css';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  tooltipId?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  className = '',
  tooltipId,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const internalTooltipId = useRef(tooltipId || `tooltip-${Math.random().toString(36).substr(2, 9)}`).current;

  const showTooltip = useCallback(() => setIsVisible(true), []);
  const hideTooltip = useCallback(() => setIsVisible(false), []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideTooltip();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, hideTooltip]);

  const triggerProps = {
    ref: triggerRef,
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    'aria-describedby': isVisible ? internalTooltipId : undefined,
  };

  // Clone the child element to add props
  const triggerElement = React.cloneElement(children, triggerProps);

  return (
    <>
      {triggerElement}
      {isVisible && content && (
        <div
          id={internalTooltipId}
          ref={tooltipRef}
          role="tooltip"
          className={`${styles.tooltip} ${styles[position]} ${className}`}
        >
          {content}
        </div>
      )}
    </>
  );
};

export default Tooltip; 